
import React from "react";
import { useGoalsQuery } from "./queries/use-goals-query";
import { useAddGoalMutation } from "./mutations/use-add-goal-mutation";
import { useUpdateGoalMutation } from "./mutations/use-update-goal-mutation";
import { useDeleteGoalMutation } from "./mutations/use-delete-goal-mutation";
import { Goal } from "./goal-types";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/hooks/auth/use-user";
import { toast } from "@/hooks/errors/use-toast";


export default function useGoalsState() {
  const { data: goals = [], isLoading } = useGoalsQuery();
  const { mutateAsync: addGoal } = useAddGoalMutation();
  const { mutateAsync: updateGoal } = useUpdateGoalMutation();
  const { mutateAsync: deleteGoal } = useDeleteGoalMutation();
  const navigate = useNavigate();
  const { user } = useUser();

  // No more hardcoded initialGoals - fetch from database

  // Completed/active goals - derived from DB
  const activeGoals = goals.filter(goal => !goal.isCompleted);
  const completedGoals = goals.filter(goal => goal.isCompleted);

  const handleGoalClick = (goalId: string) => {
    console.log('[GoalClick] 🚀 Project card clicked, goalId:', goalId);
    
    // Find the goal to get its project ID
    const goal = goals.find(g => g.id === goalId);
    console.log('[GoalClick] 🔍 Found goal:', {
      goalId,
      goal: goal ? {
        id: goal.id,
        title: goal.title,
        projectId: goal.projectId,
        hasProjectId: !!goal.projectId
      } : null
    });
    
    if (!goal) {
      console.error('[GoalClick] ❌ Goal not found:', goalId);
      toast({
        title: "Navigation Error",
        description: "Goal not found. Please refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }
    
    // Navigate to project route
    if (goal?.projectId) {
      const targetUrl = `/project/${goal.projectId}/tasks`;
      console.log('[GoalClick] ✅ Navigating to project tasks:', {
        goalId,
        projectId: goal.projectId,
        targetUrl
      });
      navigate(targetUrl);
    } else {
      console.error('[GoalClick] ❌ Goal missing projectId:', {
        goalId,
        goalTitle: goal.title,
        goalData: goal
      });
      toast({
        title: "Navigation Error",
        description: `Goal "${goal.title}" is missing project information. The project data may be corrupted.`,
        variant: "destructive",
      });
    }
  };

  const handleReorder = async (reorderedGoals: Goal[]) => {
    // Reorder in DB using the ids and their index
    if (!user) return;
    // Usually bulk update for goal_order
    for (let i = 0; i < reorderedGoals.length; i++) {
      await updateGoal({ goalId: reorderedGoals[i].id, updates: { goalOrder: i + 1 } });
    }
  };

  const handleClearCompletedGoals = async (e: React.MouseEvent) => {
    e.stopPropagation();
    for (const goal of completedGoals) {
      await deleteGoal(goal.id);
    }
  };

  const toggleGoalCompletion = async (goalId: string) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;
    await updateGoal({
      goalId,
      updates: { isCompleted: !goal.isCompleted, progress: !goal.isCompleted ? 100 : 0 }
    });
  };

  // Not needed, if using latest query data for showing/hiding
  const [showCompletedGoals, setShowCompletedGoals] = React.useState(true);

  return {
    goals,
    activeGoals,
    completedGoals,
    showCompletedGoals,
    isLoading,
    handleGoalClick,
    handleReorder,
    handleClearCompletedGoals,
    toggleGoalCompletion,
    setShowCompletedGoals
  };
}
